/**
 * Sticky product module for the shared CRA on :3002 (ZTASK / ZORDER / ZASSET / ZCUSTOMER).
 * Set once per tab session from launcher hash `app=` or inferred from entry path.
 */
const STORAGE_KEY = "zActiveApp";

const VALID = new Set(["ZTASK", "ZORDER", "ZASSET", "ZCUSTOMER", "ZCOMPLIANCE"]);

export function normalizeAppName(name) {
  if (!name) return null;
  const key = String(name).trim().toUpperCase().replace(/\s+/g, "");
  if (VALID.has(key)) return key;
  if (key === "ZTASKS") return "ZTASK";
  return null;
}

export function inferAppFromPath(pathname = "") {
  const p = String(pathname || "").toLowerCase();
  // ZORDER-only surfaces
  if (
    p.startsWith("/work-orders") ||
    p.startsWith("/invoices") ||
    p.startsWith("/ar") ||
    p.startsWith("/order-dashboard") ||
    p.startsWith("/quotations") ||
    p.startsWith("/ops-dashboard")
  ) {
    return "ZORDER";
  }
  // Shared across ZTASK + ZORDER menus — never flip sticky app from these paths.
  // (Previously /project inferred ZTASK and wiped ZOrder menus when opening Projects.)
  if (
    p.startsWith("/project") ||
    p.startsWith("/assets") ||
    p.startsWith("/customers") ||
    p.startsWith("/settings") ||
    p.startsWith("/organization") ||
    p.startsWith("/platform") ||
    p.startsWith("/reports") ||
    p.startsWith("/search") ||
    p.startsWith("/audit") ||
    p.startsWith("/access-denied") ||
    p.startsWith("/login")
  ) {
    return null;
  }
  if (p.startsWith("/compliance")) {
    return "ZCOMPLIANCE";
  }
  // ZTASK-only surfaces
  if (p.startsWith("/tasks") || p === "/" || p.startsWith("/dashboard")) {
    return "ZTASK";
  }
  return null;
}

export function setActiveApp(appName) {
  const key = normalizeAppName(appName);
  if (!key) return null;
  try {
    sessionStorage.setItem(STORAGE_KEY, key);
  } catch (_) {
    /* ignore */
  }
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent("z-active-app", { detail: key }));
    } catch (_) {
      /* ignore */
    }
  }
  return key;
}

export function getActiveApp() {
  try {
    const stored = normalizeAppName(sessionStorage.getItem(STORAGE_KEY));
    if (stored) return stored;
  } catch (_) {
    /* ignore */
  }
  if (typeof window !== "undefined") {
    const inferred = inferAppFromPath(window.location.pathname);
    if (inferred) {
      setActiveApp(inferred);
      return inferred;
    }
  }
  return "ZTASK";
}

export function isApp(...names) {
  const active = getActiveApp();
  return names.some((n) => normalizeAppName(n) === active);
}

export function getAppHomePath(appName = getActiveApp()) {
  switch (normalizeAppName(appName)) {
    case "ZORDER":
      return "/order-dashboard";
    case "ZASSET":
      return "/assets";
    case "ZCUSTOMER":
      return "/customers";
    case "ZCOMPLIANCE":
      return "/compliance";
    case "ZTASK":
    default:
      return "/dashboard";
  }
}

export function getReportModuleFilter(appName = getActiveApp()) {
  switch (normalizeAppName(appName)) {
    case "ZORDER":
      return ["zorder", "workorder"];
    case "ZASSET":
      return ["zasset", "asset"];
    case "ZCUSTOMER":
      return ["zcustomer", "customer"];
    case "ZTASK":
    default:
      return ["ztask"];
  }
}

export function getDefaultReportModuleName(appName = getActiveApp()) {
  switch (normalizeAppName(appName)) {
    case "ZORDER":
      return "ZOrder";
    case "ZASSET":
      return "ZAsset";
    case "ZCUSTOMER":
      return "ZCustomer";
    case "ZTASK":
    default:
      return "ZTask";
  }
}

/** Soft isolation: redirect if current path belongs to another product. */
export function getCrossModuleRedirect(pathname, appName = getActiveApp()) {
  const app = normalizeAppName(appName) || "ZTASK";
  const p = String(pathname || "").toLowerCase();

  // Shared surfaces (available across modules)
  if (
    p.startsWith("/settings") ||
    p.startsWith("/organization") ||
    p.startsWith("/platform") ||
    p.startsWith("/reports") ||
    p.startsWith("/login") ||
    p.startsWith("/project") ||
    p.startsWith("/assets") ||
    p.startsWith("/customers") ||
    p.startsWith("/search") ||
    p.startsWith("/audit") ||
    p.startsWith("/access-denied")
  ) {
    // Maintenance reports belong to ZTask surface
    if (p.startsWith("/reports/maintenance") && app !== "ZTASK") {
      return getAppHomePath(app);
    }
    // Work types are ZOrder master data
    if (p.startsWith("/organization/work-types") && app !== "ZORDER") {
      return getAppHomePath(app);
    }
    return null;
  }

  const isTaskPath =
    p === "/" ||
    p.startsWith("/tasks") ||
    p.startsWith("/dashboard");
  const isOrderPath =
    p.startsWith("/work-orders") ||
    p.startsWith("/invoices") ||
    p.startsWith("/ar") ||
    p.startsWith("/order-dashboard") ||
    p.startsWith("/quotations") ||
    p.startsWith("/ops-dashboard");
  const isCompliancePath = p.startsWith("/compliance");
  const isAssetPath = false; // assets shared on both ZTASK and ZORDER menus
  const isCustomerPath = false;

  if (app === "ZCOMPLIANCE" && !isCompliancePath && (isTaskPath || isOrderPath)) {
    return getAppHomePath("ZCOMPLIANCE");
  }
  if (app !== "ZCOMPLIANCE" && isCompliancePath) {
    return getAppHomePath(app);
  }
  if (app === "ZORDER" && isTaskPath) return getAppHomePath("ZORDER");
  if (app === "ZTASK" && isOrderPath) return getAppHomePath("ZTASK");
  if (app === "ZASSET" && (isTaskPath || isOrderPath)) return getAppHomePath("ZASSET");
  if (app === "ZCUSTOMER" && (isTaskPath || isOrderPath)) return getAppHomePath("ZCUSTOMER");
  return null;
}
